import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Bug,
  Wrench,
  Lightbulb,
  Upload,
  AlertCircle,
  CheckCircle,
  Loader2,
  ChevronDown
} from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { FeedbackType, FeedbackPriority, FeedbackCategory } from '../../types';
import { feedbackService } from '../../services/feedbackService';
import { syncFeedbackToEchoBrain } from '../../services/feedbackWithEchoBrain';
import { toastService } from '../../services/toastService';
import { Button } from '@/components/shared';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage: string;
  currentUserId?: string;
  currentUserName?: string;
  currentUserEmail?: string;
  currentUserAvatar?: string;
  orgId?: string;
  /** Ticket-Übersicht (ehem. Hauptnavigation „Ticket-System“) */
  onOpenTicketOverview?: () => void;
}

const FEEDBACK_TYPES: { id: FeedbackType; label: string; icon: React.ElementType; color: string; bgColor: string; description: string }[] = [
  {
    id: 'bug',
    label: 'Bug melden',
    icon: Bug,
    color: 'text-danger',
    bgColor: 'bg-danger-soft border-danger hover:bg-danger-medium hover:border-danger',
    description: 'Etwas funktioniert nicht richtig'
  },
  {
    id: 'change_request',
    label: 'Änderungswunsch',
    icon: Wrench,
    color: 'text-warning',
    bgColor: 'bg-warning-soft border-warning hover:bg-warning-soft hover:border-warning',
    description: 'Bestehendes Feature verbessern'
  },
  {
    id: 'feature_request',
    label: 'Feature Vorschlag',
    icon: Lightbulb,
    color: 'text-accent',
    bgColor: 'bg-info-soft border-accent hover:bg-info-soft hover:border-accent',
    description: 'Neue Funktion vorschlagen'
  },
];

const CATEGORIES: { id: FeedbackCategory; label: string }[] = [
  { id: 'ui', label: 'Benutzeroberfläche' },
  { id: 'performance', label: 'Performance' },
  { id: 'functionality', label: 'Funktionalität' },
  { id: 'data', label: 'Daten' },
  { id: 'integration', label: 'Integration' },
  { id: 'ai_agents', label: 'AI Agents + Agent Hub' },
  { id: 'other', label: 'Sonstiges' },
];

const PRIORITIES: { id: FeedbackPriority; label: string; color: string }[] = [
  { id: 'low', label: 'Niedrig', color: 'bg-success-soft text-success border-success hover:bg-success-soft' },
  { id: 'medium', label: 'Mittel', color: 'bg-warning-soft text-warning border-warning hover:bg-warning-soft' },
  { id: 'high', label: 'Hoch', color: 'bg-danger-soft text-danger border-danger hover:bg-danger-soft' },
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  currentPage,
  currentUserId,
  currentUserName,
  currentUserEmail,
  currentUserAvatar,
  orgId,
  onOpenTicketOverview,
}) => {
  const { t: tn } = useTranslation('navigation');
  useEscapeKey(onClose, isOpen);

  const [step, setStep] = useState<'type' | 'form'>('type');
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
  const [category, setCategory] = useState<FeedbackCategory>('other');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<FeedbackPriority>('medium');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [syncToEchoBrain, setSyncToEchoBrain] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [show, setShow] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = useCallback(() => {
    setStep('type');
    setFeedbackType(null);
    setCategory('other');
    setTitle('');
    setDescription('');
    setPriority('medium');
    setScreenshotFile(null);
    setScreenshotPreview(null);
    setSyncToEchoBrain(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setShow(true), 50);
    } else {
      setShow(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      resetForm();
      onClose();
    }, 200);
  };

  const handleTypeSelect = (type: FeedbackType) => {
    setFeedbackType(type);
    setStep('form');
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setScreenshotFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setScreenshotPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setScreenshotFile(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!feedbackType || !title.trim() || !description.trim() || !currentUserId || !orgId) {
      toastService.warning('Bitte fülle alle Pflichtfelder aus.');
      return;
    }

    setIsSubmitting(true);
    try {
      let screenshotUrl: string | undefined;

      if (screenshotFile) {
        screenshotUrl = await feedbackService.uploadScreenshot(screenshotFile, orgId);
      }

      const feedbackPayload = {
        orgId,
        userId: currentUserId,
        userName: currentUserName || 'Unbekannt',
        userEmail: currentUserEmail || '',
        userAvatar: currentUserAvatar,
        type: feedbackType,
        category,
        title: title.trim(),
        description: description.trim(),
        priority,
        currentPage,
        screenshotUrl,
      };

      const feedbackId = await feedbackService.submitFeedback(feedbackPayload);

      let echoBrainSyncFailed = false;

      if (syncToEchoBrain) {
        try {
          await syncFeedbackToEchoBrain({
            id: feedbackId,
            ...feedbackPayload,
            appSection: sectionName,
            pageName,
          });
        } catch (syncError) {
          echoBrainSyncFailed = true;
          console.warn('[EchoBrain] Feedback sync failed:', syncError);
        }
      }

      if (echoBrainSyncFailed) {
        toastService.warning('Feedback gespeichert, aber Echo-Brain Sync ist fehlgeschlagen.');
      } else if (syncToEchoBrain) {
        toastService.success('Feedback gesendet und mit Echo-Brain synchronisiert.');
      } else {
        toastService.success('Danke für dein Feedback! Wir kümmern uns darum.');
      }
      handleClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toastService.error('Feedback konnte nicht gesendet werden. Bitte versuche es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const pageName = feedbackService.getPageDisplayName(currentPage);
  const sectionName = feedbackService.getAppSection(currentPage);

  return (
    <div className={`fixed inset-0 z-[10001] flex items-center justify-center transition-all duration-200 ${show ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      
      {/* Modal */}
      <div className={`relative bg-bg-card rounded-2xl shadow-heavy w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden transition-all duration-200 ${show ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-subtle">
          <div>
            <h2 className="text-lg font-bold text-txt">
              {step === 'type' ? 'Feedback geben' : FEEDBACK_TYPES.find(t => t.id === feedbackType)?.label}
            </h2>
            <p className="text-xs text-txt-tertiary mt-0.5">
              Seite: {sectionName} &gt; {pageName}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-bg-surface rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-txt-tertiary" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 'type' ? (
            /* Step 1: Type Selection */
            <div className="p-6 space-y-3">
              <p className="text-sm text-txt-secondary mb-4">Was möchtest du uns mitteilen?</p>
              {FEEDBACK_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleTypeSelect(type.id)}
                  className={`w-full flex items-center space-x-4 p-4 rounded-xl border-2 transition-all duration-200 ${type.bgColor}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type.color} bg-bg-card shadow-card`}>
                    <type.icon className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className={`font-semibold ${type.color}`}>{type.label}</div>
                    <div className="text-xs text-txt-tertiary">{type.description}</div>
                  </div>
                </button>
              ))}
              {onOpenTicketOverview && currentPage !== 'feedback' && (
                <div className="pt-4 mt-1 border-t border-subtle">
                  <Button variant="ghost" className="w-full" onClick={onOpenTicketOverview}>
                    {tn('openTicketSystem')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Step 2: Form */
            <div className="p-6 space-y-4">
              {/* Back button */}
              <button
                onClick={() => setStep('type')}
                className="text-sm text-txt-tertiary hover:text-txt-secondary transition-colors flex items-center space-x-1"
              >
                <span>&larr;</span>
                <span>Typ ändern</span>
              </button>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Kategorie</label>
                <div className="relative">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
                    className="w-full px-3 py-2.5 rounded-xl border border-default bg-bg-card text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent appearance-none pr-10"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-muted pointer-events-none" />
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">
                  Titel <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={
                    feedbackType === 'bug' ? 'z.B. Button reagiert nicht auf Klick...' :
                    feedbackType === 'change_request' ? 'z.B. Filter sollte gespeichert werden...' :
                    'z.B. Dark Mode für die App...'
                  }
                  className="w-full px-3 py-2.5 rounded-xl border border-default text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                  maxLength={120}
                />
                <div className="text-right text-xs text-txt-muted mt-1">{title.length}/120</div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">
                  Beschreibung <span className="text-danger">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={
                    feedbackType === 'bug' ? 'Beschreibe den Fehler so genau wie möglich. Was hast du erwartet? Was ist stattdessen passiert?' :
                    feedbackType === 'change_request' ? 'Was sollte geändert werden und warum? Wie stellst du dir die Verbesserung vor?' :
                    'Beschreibe die gewünschte Funktion. Welches Problem würde sie lösen?'
                  }
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-xl border border-default text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-none"
                  maxLength={2000}
                />
                <div className="text-right text-xs text-txt-muted mt-1">{description.length}/2000</div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Priorität</label>
                <div className="flex space-x-2">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPriority(p.id)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all duration-200 ${
                        priority === p.id
                          ? `${p.color} ring-2 ring-offset-1 ring-accent/20`
                          : 'bg-bg-surface text-txt-tertiary border-default hover:bg-bg-surface'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Echo-Brain Sync */}
              <label
                className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                  syncToEchoBrain
                    ? 'border-accent/40 bg-accent/10'
                    : 'border-default bg-bg-surface hover:border-accent/30'
                }`}
              >
                <input
                  type="checkbox"
                  checked={syncToEchoBrain}
                  onChange={(e) => setSyncToEchoBrain(e.target.checked)}
                  disabled={isSubmitting}
                  className="mt-1 h-4 w-4 rounded border-default accent-accent"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm font-semibold text-txt">
                    <AlertCircle className="h-4 w-4 text-accent" />
                    <span>Mit Echo-Brain synchronisieren</span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-txt-tertiary">
                    Erstellt beim Absenden zusätzlich eine Echo-Brain Aufgabe für diesen Bug, diese Änderung oder dieses Feature.
                  </p>
                </div>
              </label>

              {/* Screenshot Upload */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Screenshot (optional)</label>
                {!screenshotPreview ? (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center justify-center space-x-2 py-3 rounded-xl border-2 border-dashed border-default text-sm text-txt-tertiary hover:border-accent hover:text-accent transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Bild hochladen</span>
                  </button>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-default">
                    <img src={screenshotPreview} alt="Screenshot" className="w-full max-h-40 object-cover" />
                    <button
                      onClick={removeScreenshot}
                      className="absolute top-2 right-2 p-1 bg-danger text-txt-inverted rounded-full hover:bg-danger-soft transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  className="hidden"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'form' && (
          <div className="px-6 py-4 border-t border-subtle flex items-center justify-between bg-bg-surface">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm text-txt-secondary hover:text-txt transition-colors"
              disabled={isSubmitting}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !title.trim() || !description.trim()}
              className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-accent to-accent text-txt-inverted text-sm font-medium rounded-xl hover:from-accent hover:to-accent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-card hover:shadow-card"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sende...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Feedback senden</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
